import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ShoppingCart } from "lucide-react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/constants";
import { toast } from "sonner";

export default function ChargeModal({ debt, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: "", description: "", date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (debt) setForm({ amount: "", description: "", date: new Date().toISOString().slice(0, 10) }); }, [debt]);
  if (!debt) return null;

  const amt = parseFloat(form.amount) || 0;
  const newBal = debt.balance + amt;
  const newUtil = debt.credit_limit > 0 ? Math.round((newBal / debt.credit_limit) * 1000) / 10 : null;

  const save = async (e) => {
    e.preventDefault();
    if (amt <= 0) { toast.error("Enter a positive amount"); return; }
    setSaving(true);
    try {
      await api.post(`/debts/${debt.id}/charges`, { amount: amt, description: form.description, date: form.date });
      toast.success(`Charged ${fmt(amt)} to ${debt.name}`, { description: newUtil != null ? `Balance ${fmt(newBal)} · utilization now ${newUtil}%` : `Balance ${fmt(newBal)}` });
      onSaved?.(); onClose?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not log charge"); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={save} data-testid="charge-modal"
        className="w-full max-w-md bg-card border border-border rounded-2xl p-6 shadow-2xl fade-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-rose-400" /> Log a charge · {debt.name}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-accent"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Amount</span>
            <input required autoFocus type="number" min="0.01" step="0.01" data-testid="charge-amount-input" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className={inp} placeholder="42.17" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Date</span>
            <input type="date" data-testid="charge-date-input" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className={inp} />
          </label>
          <label className="block col-span-2">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">What was it?</span>
            <input data-testid="charge-desc-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inp} placeholder="Groceries, gas, Amazon…" />
          </label>
        </div>
        <div className="mt-3 p-3 rounded-lg bg-accent/40 border border-border/60 text-xs flex items-center justify-between" data-testid="charge-preview">
          <span className="text-muted-foreground">New balance</span>
          <span className="font-mono font-semibold">{fmt(newBal)}{newUtil != null && <span className={`ml-2 ${newUtil < 30 ? "text-emerald-500" : newUtil < 50 ? "text-amber-500" : "text-rose-500"}`}>{newUtil}% util</span>}</span>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border hover:bg-accent text-sm transition">Cancel</button>
          <button disabled={saving} type="submit" data-testid="charge-save-btn" className="px-5 py-2 rounded-lg bg-rose-500 text-white font-semibold hover:bg-rose-600 text-sm transition disabled:opacity-60">{saving ? "Saving…" : "Log charge"}</button>
        </div>
      </form>
    </div>,
    document.body
  );
}

const inp = "w-full px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/40 focus:border-rose-500 transition mt-1";
